import React, { HTMLAttributes, createRef, useCallback, useEffect, useRef } from 'react';
import cx from 'classnames';
import styles from './DeviceNavigation.module.scss';
import { Topic } from 'api/contentful/contentTypes.cloverdotcom';
import { FlatEntry } from 'services/cms/flattenContentful';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { usePan } from 'hooks/usePan';

type DeviceNavigationProps = HTMLAttributes<HTMLDivElement> & {
  items: FlatEntry<Topic>[];
  activeIndex: number;
  onButtonClick: (index: number) => void;
  onPanNext?: () => void;
  onPanPrev?: () => void;
};

export const DeviceNavigation: React.FC<DeviceNavigationProps> = ({
  items,
  activeIndex,
  onButtonClick,
  onPanNext,
  onPanPrev,
}) => {
  const { browser } = useBreakpoints();
  const activeButtonRef = createRef<HTMLButtonElement>();
  const listRef = createRef<HTMLUListElement>();
  const rootRef = useRef<HTMLDivElement>(null);
  const { pointerRef, ...pointerEvents } = usePan({
    cssVarEl: rootRef,
    isFirstIndex: false,
    isLastIndex: false,
    onPanNext,
    onPanPrev,
  });

  const scrollButtonIntoView = useCallback(
    (buttonIndex: number) => {
      const { current: list } = listRef;

      if (!list) return;

      // prevents scrolling when large buttons are focused
      list.parentElement?.scrollTo({ left: 0 });

      const columnGap = parseFloat(window.getComputedStyle(list).columnGap);
      const btns = list.querySelectorAll<HTMLButtonElement>('button');
      const buttonsWidth = Array.from(btns)
        .slice(0, buttonIndex)
        .reduce((acc, btn) => btn.getBoundingClientRect().width + acc + columnGap, 0);

      const positionOffset = browser.is.desktop
        ? 0
        : window.innerWidth / 2 - btns[buttonIndex].offsetWidth / 2;

      if (buttonIndex === 0) list?.style.setProperty('--left-offset', `${positionOffset}px`);
      else list?.style.setProperty('--left-offset', `-${buttonsWidth - positionOffset}px`);
    },
    [browser.is, listRef],
  );

  useEffect(() => {
    activeButtonRef.current && scrollButtonIntoView(activeIndex);
  }, [activeIndex, activeButtonRef, scrollButtonIntoView]);

  if (!items.length) return null;

  return (
    <nav className={styles.deviceNavHeading} {...pointerEvents}>
      <ul ref={listRef}>
        {items.map((entry, index) => (
          <li key={entry.id}>
            <button
              ref={index === activeIndex ? activeButtonRef : undefined}
              onClick={() => onButtonClick(index)}
              className={cx(index === activeIndex && styles.active)}
              onKeyUp={() => scrollButtonIntoView(index)}
            >
              {entry.title}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
};
